import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Loader2 } from "lucide-react";
import ItemPicker from "./ItemPicker";

const REASONS = [
  "Kitchen requisition", "Expired", "Spoiled", "Burnt / prep error",
  "Damaged", "Pest", "Returned by guest", "Stock-out – urgent",
  "Event / catering", "Vendor failed to deliver", "Stock count variance", "Other",
];

export default function MovementForm({ type, title, items, vendors = [], locations, onDone, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [location, setLocation] = useState("");
  const [toLocation, setToLocation] = useState("");

  // Sync location once the locations prop arrives (parent fetches async).
  useEffect(() => {
    if (!location && locations.length > 0) {
      setLocation(locations[0]?.code || "");
    }
  }, [locations, location]);
  const [vendorId, setVendorId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);

  const isTransfer = type === "TRANSFER_OUT";
  const needsCost = ["OPENING", "PURCHASE_GRN", "PURCHASE_DIRECT"].includes(type);
  const isWastage = type === "WASTAGE";

  const addLine = (item) => {
    setLines(prev => [...prev, {
      sku: item.sku,
      item_name: item.name,
      base_uom: item.base_uom,
      purchase_uom_label: item.purchase_uom_label,
      base_per_purchase: item.base_per_purchase,
      qty: "",
      uom_entered: item.base_per_purchase ? "purchase" : "base",
      unit_cost: "",
      reason: "",
      expiry: "",
    }]);
  };

  const updateLine = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const totalValue = useMemo(() => {
    return lines.reduce((s, l) => {
      const qty = Number(l.qty) || 0;
      const cost = Number(l.unit_cost) || 0;
      const bpp = Number(l.base_per_purchase) || 1;
      const qtyBase = l.uom_entered === "purchase" ? qty * bpp : qty;
      return s + qtyBase * cost;
    }, 0);
  }, [lines]);

  const createBatch = async () => {
    if (!date || !location) { toast.error("Date and location are required"); return null; }
    if (isTransfer && !toLocation) { toast.error("Select destination location"); return null; }
    if (lines.length === 0) { toast.error("Add at least one item"); return null; }
    for (const [i, l] of lines.entries()) {
      if (!l.qty || Number(l.qty) <= 0) { toast.error(`Line ${i + 1}: qty must be > 0`); return null; }
      if (needsCost && (!l.unit_cost || Number(l.unit_cost) <= 0)) { toast.error(`Line ${i + 1}: unit cost required`); return null; }
    }
    try {
      const { data } = await api.post("/staff/inventory/v2/batches", {
        channel: "manual",
        movement_type: type,
        date,
        location,
        to_location: isTransfer ? toLocation : null,
        vendor_id: vendorId || null,
        invoice_no: invoiceNo,
        reason,
        lines: lines.map(l => ({
          sku: l.sku,
          qty: Number(l.qty),
          uom_entered: l.uom_entered,
          unit_cost: l.unit_cost ? Number(l.unit_cost) : null,
          reason: l.reason || reason,
          expiry: l.expiry || null,
        })),
      });
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create batch");
      return null;
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    const batch = await createBatch();
    setSaving(false);
    if (batch) {
      toast.success(`Draft ${batch.batch_no} saved`);
      onDone();
      onCancel();
    }
  };

  const saveAndPost = async () => {
    setPosting(true);
    const batch = await createBatch();
    if (batch) {
      try {
        const { data } = await api.post(`/staff/inventory/v2/batches/${batch.id}/post`);
        if (data.needs_approval) {
          toast.warning(`Sent to approvals: ${data.rule_hits.map(h => h.rule).join(", ")}`);
        } else {
          toast.success(`Posted — ${data.ledger_rows.length} ledger row(s)`);
        }
        onDone();
        onCancel();
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(d?.errors ? d.errors.map(e => e.detail).join(" · ") : (typeof d === "string" ? d : "Post failed"));
      }
    }
    setPosting(false);
  };

  return (
    <div data-testid="movement-form" className="space-y-4">
      {/* Header fields */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Date
          <input type="date" data-testid="mf-date" value={date} onChange={e => setDate(e.target.value)} className="tbb-inp mt-1 w-full" />
        </label>
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          {isTransfer ? "From" : "Location"}
          <select data-testid="mf-location" value={location} onChange={e => setLocation(e.target.value)} className="tbb-inp mt-1 w-full">
            {locations.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </label>
        {isTransfer && (
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            To
            <select data-testid="mf-to-location" value={toLocation} onChange={e => setToLocation(e.target.value)} className="tbb-inp mt-1 w-full">
              <option value="">— Select —</option>
              {locations.filter(l => l.code !== location).map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </label>
        )}
        {needsCost && (
          <>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Vendor
              <select data-testid="mf-vendor" value={vendorId} onChange={e => setVendorId(e.target.value)} className="tbb-inp mt-1 w-full">
                <option value="">— Select —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Invoice #
              <input data-testid="mf-invoice" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} className="tbb-inp mt-1 w-full" placeholder="Optional" />
            </label>
          </>
        )}
        {isWastage && (
          <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Reason
            <select data-testid="mf-reason" value={reason} onChange={e => setReason(e.target.value)} className="tbb-inp mt-1 w-full">
              <option value="">— Select —</option>
              {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        )}
      </div>

      {/* Item picker */}
      <ItemPicker items={items} onPick={addLine} />

      {/* Lines */}
      {lines.length > 0 && (
        <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="p-2 border-b border-[hsl(var(--rule))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] flex justify-between">
            <span>{lines.length} item{lines.length === 1 ? "" : "s"}</span>
            {needsCost && <span className="font-semibold text-[hsl(var(--ink))]">₹{totalValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>}
          </div>
          {lines.map((l, i) => (
            <div key={i} data-testid={`mf-line-${i}`} className="p-3 border-b border-[hsl(var(--rule))] last:border-b-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="font-semibold text-sm">{l.item_name}</div>
                  <div className="text-xs text-[hsl(var(--ink-soft))] font-mono">{l.sku}</div>
                </div>
                <button onClick={() => removeLine(i)} className="text-[hsl(6_62%_45%)] p-1"><Trash2 size={16} /></button>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                  Qty
                  <input type="number" step="0.01" data-testid={`mf-qty-${i}`} value={l.qty} onChange={e => updateLine(i, { qty: e.target.value })} className="tbb-inp mt-0.5 w-full text-sm" />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                  UoM
                  <select data-testid={`mf-uom-${i}`} value={l.uom_entered} onChange={e => updateLine(i, { uom_entered: e.target.value })} className="tbb-inp mt-0.5 w-full text-sm">
                    <option value="base">{l.base_uom}</option>
                    {l.base_per_purchase && <option value="purchase">{l.purchase_uom_label || "Pack"}</option>}
                  </select>
                </label>
                {needsCost && (
                  <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    Cost / {l.uom_entered === "purchase" ? (l.purchase_uom_label || "pack") : l.base_uom}
                    <input type="number" step="0.01" data-testid={`mf-cost-${i}`} value={l.unit_cost} onChange={e => updateLine(i, { unit_cost: e.target.value })} className="tbb-inp mt-0.5 w-full text-sm" />
                  </label>
                )}
              </div>
              {/* Live conversion */}
              {l.qty && Number(l.qty) > 0 && (
                <div className="mt-1 text-xs text-[hsl(var(--brass-2))]">
                  {l.uom_entered === "purchase" && l.base_per_purchase
                    ? `${l.qty} ${l.purchase_uom_label || "pack"} = ${(Number(l.qty) * l.base_per_purchase).toLocaleString("en-IN")} ${l.base_uom}`
                    : `${l.qty} ${l.base_uom}`}
                  {needsCost && l.unit_cost && ` · ₹${(l.uom_entered === "purchase" ? Number(l.qty) * Number(l.unit_cost) : Number(l.qty) * Number(l.unit_cost)).toLocaleString("en-IN")}`}
                </div>
              )}
              {isWastage && (
                <input placeholder="Line reason (optional)" value={l.reason} onChange={e => updateLine(i, { reason: e.target.value })} className="tbb-inp mt-2 w-full text-xs" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2 sticky bottom-0 bg-[hsl(var(--bg))] py-3 -mx-4 px-4 border-t border-[hsl(var(--rule))]">
        <button data-testid="mf-save-draft" onClick={saveDraft} disabled={saving || posting || lines.length === 0} className="flex-1 py-3 text-sm border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin inline" /> : "Save draft"}
        </button>
        <button data-testid="mf-save-post" onClick={saveAndPost} disabled={saving || posting || lines.length === 0} className="flex-1 py-3 text-sm btn-brass disabled:opacity-50">
          {posting ? <Loader2 size={16} className="animate-spin inline" /> : "Post"}
        </button>
      </div>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
